import { generateThemeColors } from "@tellescope/utils";
import type { CSSProperties } from "react";
import type { FormCustomization } from "@tellescope/types-models";

export interface ResolveFormCustomizationStyleArgs {
  customization?: FormCustomization;
  backgroundColor?: string;
  themeColor?: string;
  themeColorSecondary?: string;
}

/** Legacy's own default page background: a form still carrying it was never deliberately styled. */
const UNCONFIGURED_BACKGROUND = "#f4f3fa";

/**
 * Builds the inline style for the wizard's outermost element from the form's customization.
 *
 * Colors are emitted as the same `--primary`/`--secondary` CSS custom properties the portal theme
 * uses, so the wizard's existing `bg-primary` classes pick them up with no markup change and the
 * form's branding shadows the portal's for this form only. Precedence follows legacy: the form's
 * `customization` color wins, then the organization's brand color (`forms.tsx:70-85`).
 *
 * `hideBg` and the `#f4f3fa` sentinel both mean "inherit the host surface" — legacy dropped its Paper
 * wrapper for `hideBg`, which here is simply declining to set a background.
 */
export const resolveFormCustomizationStyle = ({
  customization,
  backgroundColor,
  themeColor,
  themeColorSecondary,
}: ResolveFormCustomizationStyleArgs): CSSProperties | undefined => {
  const style: Record<string, string | number> = {};

  if (!customization?.hideBg && backgroundColor && backgroundColor.toLowerCase() !== UNCONFIGURED_BACKGROUND) {
    style.backgroundColor = backgroundColor;
  }

  const primary = customization?.primaryColor || themeColor;
  if (primary) {
    const colors = generateThemeColors(primary);
    if (colors) {
      style["--primary"] = colors.cssValue;
      style["--primary-foreground"] = colors.foregroundCssValue;
    }
  }

  const secondary = customization?.secondaryColor || themeColorSecondary;
  if (secondary) {
    const colors = generateThemeColors(secondary);
    if (colors) {
      style["--secondary"] = colors.cssValue;
      style["--secondary-foreground"] = colors.foregroundCssValue;
    }
  }

  if (customization?.maxWidth) {
    style.maxWidth = customization.maxWidth;
    style.marginInline = "auto";
    style.width = "100%";
  }

  return Object.keys(style).length ? style : undefined;
};
